import styled from 'styled-components'
import VisibleOnMatch from 'Event/attendee-rules/VisibleOnMatch'
import NavButton, {
  NavButtonWithSize,
} from 'Event/Dashboard/components/NavButton'
import React from 'react'
import Published from 'Event/Dashboard/editor/views/Published'
import {Draggable, DraggableProvidedDraggableProps} from 'react-beautiful-dnd'
import {useEditMode} from 'Event/EditModeProvider'
import {DraggableOverlay} from 'lib/ui/drag-and-drop'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import {useToggle} from 'lib/toggle'
import MainNavButtonConfig from 'Event/template/NiftyFifty/Dashboard/MainNav/MainNavButton/MainNavButtonConfig'
import TabNavButton from 'Event/Dashboard/components/NavButton/TabNavButton'
import {useAttendeeVariables} from 'Event'

export const MAIN_NAV_BUTTON = 'Main Nav Button'
type MainNavButtonProps = {
  id: string
  button: NavButtonWithSize
  index: number
  isHidden?: boolean
  disableEdit?: boolean
  spacing?: number
}

export default React.memo((props: MainNavButtonProps) => {
  const isEditMode = useEditMode()
  const {flag: configVisible, toggle: toggleConfig} = useToggle()
  const {flag: showingCopyConfig, toggle: toggleCopyConfig} = useToggle()
  const v = useAttendeeVariables()

  const isPageButton = Boolean(props.button.page)
  const Button = isPageButton ? TabNavButton : NavButton
  const button = (
    <Button
      onClick={isEditMode ? toggleConfig : () => {}}
      {...props.button}
      text={v(props.button.text)}
      aria-label="main nav button"
    />
  )

  if (!isEditMode || props.disableEdit) {
    return (
      <Container
        button={props.button}
        isHidden={props.isHidden}
        spacing={props.spacing}
      >
        {button}
      </Container>
    )
  }

  return (
    <>
      <MainNavButtonConfig
        button={props.button}
        id={props.id}
        showing={configVisible}
        onClose={toggleConfig}
      />
      <MainNavButtonConfig
        button={props.button}
        showing={showingCopyConfig}
        onClose={toggleCopyConfig}
      />
      <Draggable draggableId={props.id} index={props.index}>
        {(provided) => (
          <Container
            button={props.button}
            ref={provided.innerRef}
            draggableProps={provided.draggableProps}
            isHidden={props.isHidden}
            spacing={props.spacing}
          >
            <DraggableOverlay>
              <Editable
                onEdit={toggleConfig}
                onCopy={toggleCopyConfig}
                handleProps={provided.dragHandleProps}
              >
                <>{button}</>
              </Editable>
            </DraggableOverlay>
          </Container>
        )}
      </Draggable>
    </>
  )
})

const Container = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactElement
    button: NavButtonWithSize
    draggableProps?: DraggableProvidedDraggableProps
    isHidden?: boolean
    spacing?: number
  }
>((props, ref) => {
  const {button} = props
  const {size} = button

  const widthPercent = size ? 100 * (size / 12) : 100

  return (
    <VisibleOnMatch rules={props.button.rules}>
      <Published component={props.button}>
        <Box
          ref={ref}
          isHidden={props.isHidden}
          {...props.draggableProps}
          data-testid="main nav button container"
          width={widthPercent}
          spacing={props.spacing}
        >
          {props.children}
        </Box>
      </Published>
    </VisibleOnMatch>
  )
})

const Box = styled.div<{
  isHidden?: boolean
  width: number
  spacing?: number
}>`
  ${(props) => (props.isHidden ? 'display: none;' : '')}
  width: ${(props) => props.width}%;
  margin-bottom: ${(props) =>
    props.spacing ? `${props.spacing}px` : props.theme.spacing[2]};
  margin-left: auto;
  margin-right: auto;
`
